import { assign, isEmpty, isPlainObject } from 'lodash';
import ApiColumn from '@/modules/core/app/models/api/ApiColumn';
import { RouteGroup } from '@/modules/core/app/constants/api.constants';
import { CategoryDataView } from '@/modules/core/datasource/models/CategoryDataView';
import { GenericDataModel } from '@/modules/core/app/models/GenericDataModel';
import { BaseResource } from '@/modules/core/app/resources/BaseResource';
import { GenericModel } from '@/modules/core/app/models/GenericModel';

export class CategoryDataResource extends BaseResource {
  constructor() {
    super(RouteGroup.CATEGORIES);
  }

  /**
   * @param {string} categoryId
   * @param params
   * @returns {Promise<CategoryDataView[]>}
   * */
  async getDataViews(categoryId, params = {}) {
    return super.getData(CategoryDataView, `${categoryId}/dataviews`, params);
  }

  /**
   * @param categoryId
   * @param dataViewId
   * @param params
   * @param widget
   * @param config
   * @returns {Promise<GenericDataModel[]>}
   * */
  async getData(categoryId, dataViewId, params = {}, widget = {}, config = {}) {
    let data;
    if (Array.isArray(params.fields)) {
      const fieldsQueryParam = params.fields
        // eslint-disable-next-line tap/no-raw-text-js
        .map((f) => `fields=${encodeURIComponent(f)}`)
        .join('&');
      delete params.fields;
      data = await this.apiHelper.get(
        this._buildRoute(`${categoryId}/data/${dataViewId}?${fieldsQueryParam}`),
        {
          params: assign(
            {
              aggregate: true,
            },
            params
          ),
          ...config,
        }
      );
    } else {
      data = await this.apiHelper.get(this._buildRoute(`${categoryId}/data/${dataViewId}`), {
        params: assign(
          {
            aggregate: true,
          },
          params
        ),
        ...config,
      });
    }

    const isWidgetRequest = params?.widget_request;
    /* eslint-disable tap/no-raw-text-js */
    const isStructured =
      isPlainObject(data) &&
      'currency_discrepancy' in data &&
      'show_currency' in data &&
      'data' in data;
    /* eslint-disable tap/no-raw-text-js */

    if (isWidgetRequest) {
      if (!isEmpty(widget?.metadata)) {
        widget.metadata.currency_discrepancy = false;
        widget.metadata.show_currency = false;
      }
      if (isStructured) {
        const { currency_discrepancy, show_currency, data: mainData, total_data } = data;

        if (!isEmpty(widget) && widget.metadata) {
          widget.metadata.currency_discrepancy = currency_discrepancy;
          widget.metadata.show_currency = show_currency;

          const payload = total_data ? { data: mainData, total_data } : mainData;
          return this.extractData(payload, GenericDataModel, params);
        }

        return this.extractData(mainData, GenericDataModel, params);
      }
    }
    return this.extractData(data, GenericDataModel, params);
  }

  /**
   * @param categoryId
   * @param dataViewId
   * @param params
   * @returns {Promise<ApiColumn[]>}
   * */
  async getColumns(categoryId, dataViewId, params = {}) {
    params = assign({ metadata: true }, params);
    return super.getData(ApiColumn, `${categoryId}/data/${dataViewId}`, params);
  }

  /**
   *
   * @param categoryId
   * @param dataViewId
   * @param fields
   * @param params
   * @returns {Promise<*>}
   */
  async getValuesDistinct(categoryId, dataViewId, fields, params = {}) {
    params = { ...params, fields };
    return super.getData(GenericModel, `${categoryId}/data/${dataViewId}/values/distinct`, params);
  }
}

export default new CategoryDataResource();
